/**
 * Light or dark (owner's request, 22 Oct 2026). The choice is kept on this device only; the
 * colours themselves are the design tokens in styles.css, redefined under html[data-theme=dark].
 * public/theme-init.js applies the saved choice before the first paint.
 */
import { useSyncExternalStore } from 'react';

export type Theme = 'light' | 'dark';

const KEY = 'kharcha.theme';
const BAR = { light: '#0F6E6A', dark: '#0E1615' };
const listeners = new Set<() => void>();

export function getTheme(): Theme {
  return document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light';
}

function apply(theme: Theme): void {
  const html = document.documentElement;
  html.dataset.theme = theme;
  html.style.colorScheme = theme;
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', BAR[theme]);
}

/** On start: the saved choice (light when none, or when storage is blocked). */
export function applySavedTheme(): void {
  let theme: Theme = 'light';
  try {
    if (localStorage.getItem(KEY) === 'dark') theme = 'dark';
  } catch {
    // Private mode or blocked storage: light.
  }
  apply(theme);
}

export function setTheme(theme: Theme): void {
  apply(theme);
  try {
    localStorage.setItem(KEY, theme);
  } catch {
    // Not saved: it still applies until the page is closed.
  }
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The theme now, and a way to change it; every component using it updates together. */
export function useTheme(): [Theme, (theme: Theme) => void] {
  const theme = useSyncExternalStore(subscribe, getTheme, () => 'light' as Theme);
  return [theme, setTheme];
}
